import Dexie from 'dexie'

export const db = new Dexie('ClystcoreSurveyors')

// Version 3: Added synced/remoteId fields for offline-first surveys
db.version(3).stores({
  surveys: '++id, propertyName, address, postcode, surveyorName, surveyLevel, status, synced, remoteId, createdAt, updatedAt',
  photos: '++id, surveyId, categoryCode, analyseWithAI, capturedAt, uploaded',
  settings: 'key'
}).upgrade(tx => {
  // Migration: Add synced and remoteId fields to existing surveys
  return tx.table('surveys').toCollection().modify(survey => {
    if (survey.synced === undefined) {
      survey.synced = true  // Assume existing surveys are synced
      survey.remoteId = survey.id  // Use current id as remoteId
    }
  })
})

// Version 2: Changed categoryId to categoryCode, removed categories table
// Categories are now static config in src/config/categories.js
db.version(2).stores({
  surveys: '++id, propertyName, address, postcode, surveyorName, surveyLevel, status, createdAt, updatedAt',
  photos: '++id, surveyId, categoryCode, analyseWithAI, capturedAt, uploaded',
  settings: 'key'
}).upgrade(tx => {
  // Migration: Convert old categoryId to categoryCode
  // This maps numeric IDs to category codes based on the old order
  const idToCode = {
    1: 'D1', 2: 'D2', 3: 'D3', 4: 'D4', 5: 'D5', 6: 'D6', 7: 'D7', 8: 'D8', 9: 'D9',
    10: 'E1', 11: 'E2', 12: 'E3', 13: 'E4', 14: 'E5', 15: 'E6', 16: 'E7', 17: 'E8', 18: 'E9',
    19: 'F1', 20: 'F2', 21: 'F3', 22: 'F4', 23: 'F5', 24: 'F6', 25: 'F7',
    26: 'G1', 27: 'G2', 28: 'G3'
  }

  return tx.table('photos').toCollection().modify(photo => {
    if (photo.categoryId && !photo.categoryCode) {
      photo.categoryCode = idToCode[photo.categoryId] || 'D1'
      delete photo.categoryId
    }
  })
})

// Keep version 1 for existing databases that haven't migrated yet
db.version(1).stores({
  surveys: '++id, propertyName, address, postcode, surveyorName, surveyLevel, status, createdAt, updatedAt',
  photos: '++id, surveyId, categoryId, analyseWithAI, capturedAt, uploaded',
  categories: '++id, code, section, name, order, isActive',
  settings: 'key'
})

// Initialize default settings (categories no longer needed - they're static config)
export async function initializeDatabase() {
  // Initialize default settings
  const surveyorSetting = await db.settings.get('defaultSurveyor')
  if (!surveyorSetting) {
    await db.settings.put({ key: 'defaultSurveyor', value: 'Chris I Baker' })
  }

  const apiKeySetting = await db.settings.get('openaiApiKey')
  if (!apiKeySetting) {
    await db.settings.put({ key: 'openaiApiKey', value: '' })
  }
}

// Helper functions for surveys
export const surveyDb = {
  async create(surveyData) {
    const now = new Date().toISOString()
    const survey = {
      ...surveyData,
      surveyLevel: surveyData.surveyLevel || 1,
      status: surveyData.status || 'in-progress',
      // Use passed values if provided, otherwise default to unsynced
      synced: surveyData.synced ?? false,
      remoteId: surveyData.remoteId ?? null,
      createdAt: surveyData.createdAt || now,
      updatedAt: surveyData.updatedAt || now
    }
    const id = await db.surveys.add(survey)
    return { ...survey, id }
  },

  async update(id, updates) {
    const updatedAt = new Date().toISOString()
    // Mark as needing sync if updating local data
    await db.surveys.update(id, { ...updates, updatedAt, synced: false })
    return db.surveys.get(id)
  },

  async markSynced(localId, remoteId) {
    await db.surveys.update(localId, { synced: true, remoteId })
    return db.surveys.get(localId)
  },

  async delete(id) {
    // Delete associated photos first
    await db.photos.where('surveyId').equals(id).delete()
    await db.surveys.delete(id)
  },

  async getAll() {
    return db.surveys.orderBy('createdAt').reverse().toArray()
  },

  async getById(id) {
    return db.surveys.get(id)
  },

  async getByRemoteId(remoteId) {
    return db.surveys.where('remoteId').equals(remoteId).first()
  },

  async getPending() {
    return db.surveys.filter(s => !s.synced).toArray()
  },

  async getPendingCount() {
    const pending = await db.surveys.filter(s => !s.synced).toArray()
    return pending.length
  },

  async getWithPhotos(id) {
    const survey = await db.surveys.get(id)
    if (!survey) return null
    const photos = await db.photos.where('surveyId').equals(id).toArray()
    return { ...survey, photos }
  }
}

// Helper functions for photos
export const photoDb = {
  async add(photoData) {
    const capturedAt = new Date().toISOString()
    const photo = {
      ...photoData,
      analyseWithAI: photoData.analyseWithAI !== false, // Default to true
      capturedAt,
      uploaded: false,
      r2Key: null
    }
    const id = await db.photos.add(photo)
    return { ...photo, id }
  },

  async delete(id) {
    await db.photos.delete(id)
  },

  async getBySurvey(surveyId) {
    return db.photos.where('surveyId').equals(surveyId).toArray()
  },

  async getByCategory(surveyId, categoryCode) {
    return db.photos
      .where('surveyId')
      .equals(surveyId)
      .filter(photo => photo.categoryCode === categoryCode)
      .toArray()
  },

  async updateNotes(id, notes) {
    await db.photos.update(id, { notes })
  },

  async updateAnalyseFlag(id, analyseWithAI) {
    await db.photos.update(id, { analyseWithAI })
  },

  async markUploaded(id, r2Key) {
    await db.photos.update(id, { uploaded: true, r2Key })
  }
}

// Helper functions for settings
export const settingsDb = {
  async get(key) {
    const setting = await db.settings.get(key)
    return setting?.value
  },

  async set(key, value) {
    await db.settings.put({ key, value })
  }
}

export default db
